import React, { useState, useEffect } from 'react';
import { Card, Checkbox, Select, message } from 'antd';
import type { TagItem } from './types';
import { useSessionList, useUpdateSession } from './hooks';
import {
  SaveButton,
  StyledParagraph,
  StyledCheckboxGroup,
  AdminErrorDiv,
  StyledTitle,
  SsdSelectWrapper,
} from '../ssd/style';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Child component: owns the data hooks
const SessionTagPanelBody: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ssdParam = searchParams.get('ssd_type') || 'ssd';

  const { data: sessionData, isLoading: sessionLoading, isError } = useSessionList(ssdParam);
  const { mutate: updateSession, isLoading } = useUpdateSession();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [ssdType, setSsdType] = useState<string>(ssdParam);

  useEffect(() => {
    setSelectedKeys(
      sessionData?.tagList?.filter((f: TagItem) => f.selected).map((f) => f.key) ?? [],
    );
  }, [sessionData]);

  if (sessionLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data</div>;

  const handleSave = () => {
    const maxTags = sessionData?.maxTags ?? 10;
    if (selectedKeys.length > maxTags) {
      message.error(`You can select up to ${maxTags} tags.`);
      return;
    }
    updateSession({ selectedKeys, ssdType });
  };

  return (
    <Card>
      <StyledParagraph>
        <StyledTitle>Session Tags</StyledTitle>
        Please select up to {sessionData?.maxTags} tags. The tags you choose will apply only to
        future sessions. Please also remember that
        <br />
        the same tags get applied to Session Source Data feature.
      </StyledParagraph>

      <SsdSelectWrapper>
        <Select
          defaultValue={ssdParam}
          options={sessionData.ssdOptions}
          style={{ width: 180 }}
          onChange={(value) => {
            const params = new URLSearchParams(searchParams);
            params.set('ssd_type', value);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            setSsdType(value);
          }}
        />
      </SsdSelectWrapper>

      <StyledCheckboxGroup
        value={selectedKeys}
        onChange={(checkedValues) => setSelectedKeys(checkedValues as string[])}
      >
        {sessionData?.tagList?.map((field: TagItem) => (
          <Checkbox key={field.key} value={field.key}>
            {field.name}
          </Checkbox>
        ))}
      </StyledCheckboxGroup>

      <SaveButton loading={isLoading} onClick={handleSave}>
        Save
      </SaveButton>
    </Card>
  );
};

// Parent component: only decides whether feature flag is on
const SessionTagPanel: React.FC = () => {
  const sessionTagEnabled = getFeature(featureNames.SESSION_TAG);

  if (!sessionTagEnabled) {
    return (
      <Card title="Session Source Data Configuration">
        <AdminErrorDiv>
          Current users does not have privilege to view and configure Session Source Data.
        </AdminErrorDiv>
      </Card>
    );
  }

  return <SessionTagPanelBody />;
};

export default SessionTagPanel;
